import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { FaPlus } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { ComponentRead, Reports } from '../../../api/apiTypes';
import { useFinalRanking } from '../../../api/hooks/useRanking';
import { fetchApprovalById } from '../../../api/functionApi';
import { routesMap } from '../../../routes/AppRoutes';

interface DataFromParent {
  report: Reports | undefined;
  drivers: ComponentRead[];
}

const PlusIcon = FaPlus as React.ComponentType<React.SVGProps<SVGSVGElement>>;

const RankingButton = ({ report }: DataFromParent) => {
  const navigate = useNavigate();
  const { name } = useParams();

  // 🔹 Stato locale per gestire status aggiornato
  const [localStatus, setLocalStatus] = useState(report?.status);

  useEffect(() => {
    setLocalStatus(report?.status);
  }, [report?.status]);

  const { finalRanking, loading } = useFinalRanking(report?.id);
  const hasRanking = !!finalRanking && Object.keys(finalRanking).length > 0;

  const handleCalculateClick = () => {
    Swal.fire({
      title: 'Sei sicuro di voler calcolare il ranking?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sì, calcola!',
      cancelButtonText: 'Annulla',
      reverseButtons: true,
      customClass: {
        confirmButton:
          'bg-textPrimary text-white hover:bg-textSecondary px-4 py-2 rounded ml-2',
        cancelButton:
          'bg-textSecondary text-white hover:bg-textPrimary px-4 py-2 rounded',
        actions: 'flex justify-between gap-2',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        navigate(routesMap.RANKING_REPORT(name!), { state: { id: report?.id } });
      }
    });
  };

  const handleViewClick = () => {
    navigate(routesMap.RANKING_REPORT(name!), { state: { id: report?.id } });
  };

const handleValidateDriver = async () => {
  const result = await Swal.fire({
    title: 'Sei sicuro di voler provvedere?',
    html: `
      <div style="
        max-width: 550px; 
        text-align: center; 
        margin: 0 auto; 
        color: #374151; 
        line-height: 1.5;
      ">
       <p>
      I driver che stai per validare sono stati generati da un sistema di intelligenza artificiale.
            Ti invitiamo a verificarne l’accuratezza e l’idoneità prima di confermarne la validazione.

    </p>
    <p style="margin-top: 10px;">
      Procedendo, dichiari di aver esaminato i contenuti e di assumerti la responsabilità per il loro utilizzo.
    </p>
      </div>
    `,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Conferma validazione',
    cancelButtonText: 'Annulla',
    confirmButtonColor: "#374151",
    cancelButtonColor: "#9CA3AF",
    reverseButtons: true,
    customClass: {
      confirmButton:
        'bg-textPrimary text-white hover:bg-textSecondary px-4 py-2 rounded ml-2',
      cancelButton:
        'bg-textSecondary text-white hover:bg-textPrimary px-4 py-2 rounded',
      actions: 'flex justify-between gap-2',
    },
  });

  if (result.isConfirmed && report?.id) {
    Swal.fire({
      title: 'Validazione in corso...',
      text: 'Attendere qualche secondo',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const response = await fetchApprovalById(report.id);
      //console.log('✅ Validazione completata:', response);

      Swal.fire({
        icon: 'success',
        title: 'Validazione completata!',
        text: 'I driver sono stati validati correttamente.',
        confirmButtonColor: '#374151',
      });

      setLocalStatus('approved');
    } catch (error: any) {
      Swal.fire({
  icon: 'error',
  title: 'Conflitti non risolti',
  html: `
    <div style="
      max-width: 500px;
      text-align: center;
      margin: 0 auto;
      color: #374151;
      line-height: 1.5;
    ">
      <p>
        Non è stato possibile completare la validazione perché sono ancora presenti 
        <strong>conflitti irrisolti</strong> nei driver.
      </p>
      <p style="
        margin-top: 12px;
        text-align: center;
      ">
        Ti invitiamo a risolverli prima di procedere con la validazione.
      </p>
    </div>
  `,
  confirmButtonColor: '#374151',
});

    }
  }
};


  // 🔹 Usa localStatus al posto di report.status
  const showValidateDriver =
    localStatus === 'empty' ||
    localStatus === 'generated' ||
    localStatus === 'edited';

  const showRankingButton =
    localStatus === 'approved' ||
    localStatus === 'drafted' ||
    localStatus === 'completed';

return (
  <div className="flex gap-3">
    {showValidateDriver && (
      <button
        onClick={handleValidateDriver}
        disabled={loading}
        className="bg-textPrimary text-white hover:bg-textSecondary px-4 py-2 rounded-lg text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Valida Driver
      </button>
    )}

    {showRankingButton && (
      <button
        onClick={hasRanking ? handleViewClick : handleCalculateClick}
        disabled={loading}
        className="bg-textPrimary text-white hover:bg-textSecondary px-4 py-2 rounded-lg text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {hasRanking ? 'Visualizza Ranking' : 'Calcola Ranking'}
        <PlusIcon className="ml-2" />
      </button>
    )}
  </div>
);

};

export default RankingButton;
